import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import useScreenSize from './useScreenSize';

type UseStickyHeaderProps = {
  containerRef?: React.RefObject<HTMLDivElement>;
  isHomePage?: boolean;
  isSmall?: boolean;
  isAdPage?: boolean;
  headerId?: string;
};

const FIX_ON_OFFSET = 10;
const FIX_OFF_OFFSET = 5;
const FLOAT_BUTTON_THRESHOLD = 300;

const isEditableElement = (element: Element | null): boolean => {
  if (!element) return false;

  const { tagName } = element;
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true;
  }

  const htmlElement = element as HTMLElement;
  return htmlElement.isContentEditable;
};

const updateHeaderVisibility = (headerId: string, isFixed: boolean): void => {
  const topHeader = headerId ? document.getElementById(headerId) : null;
  if (!topHeader) return;

  topHeader.style.opacity = isFixed ? '0' : '1';
  topHeader.style.pointerEvents = isFixed ? 'none' : '';
};

export const useStickyHeader = ({
  containerRef,
  isHomePage = false,
  headerId = 'popup_header',
  isAdPage = false
}: UseStickyHeaderProps) => {
  const { isSmall } = useScreenSize();

  const [isFixed, setIsFixed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [showFloatButton, setShowFloatButton] = useState(false);
  const [initialPosition, setInitialPosition] = useState(0);
  const [isScrollLocked, setIsScrollLocked] = useState(false);

  const router = useRouter();
  const isNavigatingRef = useRef(false);
  const lastScrollYRef = useRef(0);
  const tickingRef = useRef(false);

  useEffect(() => {
    if (containerRef?.current && !isFixed) {
      const position = containerRef.current.getBoundingClientRect();
      setInitialPosition(position.top + window.pageYOffset);
    }
  }, [containerRef?.current, isSmall, isFixed]);

  useEffect(() => {
    if (containerRef?.current && !isFixed) {
      setHeaderHeight(containerRef.current?.clientHeight || 0);
    }
  }, [containerRef?.current?.clientHeight, isFixed, isSmall]);

  useEffect(() => {
    const handleRouteChangeStart = () => {
      isNavigatingRef.current = true;
    };

    const handleRouteChangeEnd = () => {
      isNavigatingRef.current = false;
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);
    router.events.on('routeChangeComplete', handleRouteChangeEnd);
    router.events.on('routeChangeError', handleRouteChangeEnd);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
      router.events.off('routeChangeComplete', handleRouteChangeEnd);
      router.events.off('routeChangeError', handleRouteChangeEnd);
    };
  }, [router.events]);

  useEffect(() => {
    if (!isSmall) return;

    const handleFocusChange = () => {
      if (isNavigatingRef.current) return;

      requestAnimationFrame(() => {
        const { activeElement } = document;
        const shouldLock = isEditableElement(activeElement);
        setIsScrollLocked(prevLocked =>
          prevLocked === shouldLock ? prevLocked : shouldLock
        );
      });
    };

    document.addEventListener('focusin', handleFocusChange, true);
    document.addEventListener('focusout', handleFocusChange, true);

    return () => {
      document.removeEventListener('focusin', handleFocusChange, true);
      document.removeEventListener('focusout', handleFocusChange, true);
    };
  }, [isSmall]);

  const update = useCallback(() => {
    const banner = document.getElementById('js-lotriver-desk-top-banner');
    const bannerHeight = banner?.offsetHeight || 0;

    const isLocked =
      document.documentElement.style.position === 'fixed' ||
      document.body.style.overflow === 'hidden';

    const scrollY = isLocked ? lastScrollYRef.current : window.pageYOffset;
    const isSearchResultPage = !isHomePage && !isAdPage;

    const threshold = headerHeight + (isSearchResultPage && isSmall ? 20 : 100);

    const fixOn = isAdPage
      ? initialPosition + bannerHeight
      : threshold + FIX_ON_OFFSET;
    const fixOff = isAdPage
      ? scrollY <= initialPosition + bannerHeight
      : scrollY < threshold - FIX_OFF_OFFSET;

    const isScrollBlocked =
      getComputedStyle(document.body).overflow === 'hidden' ||
      getComputedStyle(document.documentElement).overflow === 'hidden';

    const isFixedHeaderWithOpenedModal = isFixed && !scrollY && isScrollBlocked;

    if (
      (!isFixed && scrollY > fixOn && !isScrollLocked) ||
      isFixedHeaderWithOpenedModal
    ) {
      setIsFixed(true);
    } else if (isFixed && fixOff) setIsFixed(false);

    setShowFloatButton(prevShow => {
      const shouldShow = scrollY > FLOAT_BUTTON_THRESHOLD;
      return prevShow === shouldShow ? prevShow : shouldShow;
    });

    lastScrollYRef.current = scrollY;
    tickingRef.current = false;
  }, [
    headerHeight,
    isFixed,
    isHomePage,
    isSmall,
    isScrollLocked,
    initialPosition,
    isAdPage
  ]);

  useEffect(() => {
    if (isHomePage && !isSmall) return;

    requestAnimationFrame(() => {
      update();
    });
  }, [update, isHomePage, isSmall]);

  useEffect(() => {
    if (isHomePage && !isSmall) return;

    const handleScroll = () => {
      if (tickingRef.current || (isHomePage && !isSmall)) return;
      tickingRef.current = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage, isSmall, update]);

  useEffect(() => {
    updateHeaderVisibility(headerId, isFixed);
  }, [isFixed, headerId, isSmall]);

  return {
    isFixed,
    headerHeight,
    showFloatButton,
    lastScrollY: lastScrollYRef.current
  };
};
